"use server";

import { ensureBexioLink } from "@/lib/bexio/auto-link";
import { takenSlots } from "./taken-slots";
import { z } from "zod";
import { defineAction } from "@/lib/define-action";
import { prisma } from "@/lib/prisma";
import { queueTimeEntrySync } from "@/lib/bexio/queue";
import { CACHE_TAGS } from "@/lib/cache";
import { computePackageBudgetAndContribution, getWeekDays } from "@/lib/bexio/my-time";
import type { RawGridEntryInput } from "./week-grid-calc";
import {
  computeStartAndEndTimes, shiftPastOverlaps,
  computeMostFrequentDuration, byMostUsed,
} from "./time-calc";
import type {
  PackageOption,
  ActivityOption,
  SidePanelItem,
  TimeTemplateItem,
  TimeEntryDetails,
  TimeEntryHistoryItem,
} from "./types";
import { can } from "@/lib/access-rules";
import {
  checkTimeEntryEditability,
  getEditWindowDays,
  isWorkPackageClosed,
} from "./time-edit-window";
import { evaluatePackageBudget } from "./budget-alerts";
import { notify } from "@/lib/notify";
import { usersWithPermission } from "@/lib/access";
import { setCardStatus } from "@/lib/projects/card-actions";
import { budgetClosedByProjects, CLOSED_PROJECTS_MESSAGE, entryProjectId, OPEN_FOR_TIME } from "@/lib/projects/entry-project";

const createTimeEntrySchema = z.object({
  date: z.string().regex(/^\d{4}-\d{2}-\d{2}$/, "Invalid date format"),
  minutes: z.number().int().positive("Duration must be positive"),
  workPackageId: z.number().int().positive("Budget is required"),
  activityId: z.number().int().positive().optional(),
  billable: z.boolean().optional(),
  note: z.string().min(1, "Note is required"),
  startTime: z.string().regex(/^\d{2}:\d{2}$/).optional(),
  endTime: z.string().regex(/^\d{2}:\d{2}$/).optional(),
  ticketId: z.number().int().positive().optional(),
  escalationId: z.number().int().positive().optional(),
  articleId: z.number().int().positive().optional(),
  cardId: z.number().int().positive().optional(),
  hubProjectId: z.number().int().positive().optional(),
});

export const createTimeEntryFormAction = defineAction({
  guard: "time:log",
  input: createTimeEntrySchema,
  run: async ({ userId, access }, input: z.input<typeof createTimeEntrySchema>) => {
    // SPEC §3.4 & C6: An unmapped user is stopped at save with the exact message.
    const mapping = await prisma.bexioUserMapping.findUnique({
      where: { userId },
    });

    if (!mapping) {
      throw new Error(
        "Your Hub account isn't linked to Bexio yet. Ask your manager to link your account or give you Bexio access."
      );
    }

    // SPEC §4.2 & D48: No time without a package/project.
    const pkg = await prisma.workPackage.findUnique({
      where: { id: input.workPackageId },
      include: { bexioProject: true },
    });
    if (!pkg) {
      throw new Error("Budget not found");
    }
    if (pkg.archivedAt) {
      throw new Error("Cannot book time to a closed budget");
    }
    if (await budgetClosedByProjects(pkg.id)) {
      throw new Error(CLOSED_PROJECTS_MESSAGE);
    }

    // SPEC §4.2 & D56: Billable is inherited; only PM manager/admin can change it.
    const isPm =
      access.roleIsAdmin ||
      can(access, "pm:settings_manage") ||
      can(access, "projects:manage");

    const billable = isPm ? (input.billable ?? true) : true;

    // SPEC §4.2 & D12: Compute consistent start and end times.
    const dateObj = new Date(input.date + "T00:00:00.000Z");

    const times = shiftPastOverlaps(computeStartAndEndTimes({
      minutes: input.minutes,
      customStartTime: input.startTime,
      customEndTime: input.endTime,
    }), await takenSlots(userId, dateObj));

    const [sh, sm] = times.startTime.split(":").map(Number);
    const startDateTime = new Date(dateObj);
    startDateTime.setUTCHours(sh, sm, 0, 0);

    const [eh, em] = times.endTime.split(":").map(Number);
    const endDateTime = new Date(dateObj);
    endDateTime.setUTCHours(eh, em, 0, 0);

    const hubProjectId = await entryProjectId({
      workPackageId: input.workPackageId,
      hubProjectId: input.hubProjectId,
      cardId: input.cardId,
    });

    const entry = await prisma.timeEntry.create({
      data: {
        userId,
        date: dateObj,
        minutes: input.minutes,
        workPackageId: input.workPackageId,
        hubProjectId,
        activityId: input.activityId,
        billable,
        note: input.note.trim(),
        startTime: startDateTime,
        endTime: endDateTime,
        ticketId: input.ticketId,
        escalationId: input.escalationId,
        articleId: input.articleId,
        cardId: input.cardId,
        source: "HUB",
        syncStatus: "QUEUED",
      },
    });

    // Record initial history (SPEC §4.4, §13)
    await prisma.timeEntryHistory.create({
      data: {
        timeEntryId: entry.id,
        changedBy: userId,
        field: "created",
        newValue: `${input.minutes} min on package #${input.workPackageId}`,
        editNote: "Initial entry",
      },
    });

    // Queue background sync to Bexio (SPEC §3.3, D31)
    await queueTimeEntrySync(entry.id);

    // Evaluate budget thresholds, alerts and over-budget flag (SPEC §4.5)
    await evaluatePackageBudget(input.workPackageId);

    const serializedEntry: RawGridEntryInput = {
      id: entry.id,
      date: input.date,
      startTime: times.startTime,
      endTime: times.endTime,
      minutes: entry.minutes,
      note: entry.note,
      syncStatus: entry.syncStatus,
      isAvailability: entry.isAvailability,
      isOverBudget: entry.isOverBudget,
      source: entry.source,
      billable: entry.billable,
      workPackageId: entry.workPackageId,
      activity: input.activityId ? { id: input.activityId, name: "" } : null,
      bexioMessage: null,
      bexioId: entry.bexioId,
    };

    return { ok: true, entryId: entry.id, entry: serializedEntry };
  },
  tag: CACHE_TAGS.settings,
  paths: (out, input) => {
    const list = ["/bexio"];
    if (input.ticketId) list.push(`/support-tickets/${input.ticketId}`);
    if (input.escalationId) list.push(`/escalations/${input.escalationId}`);
    if (input.cardId) {
      list.push("/projects/my-tasks");
      list.push("/projects");
    }
    return list;
  },
});

export const getTimeFormDataAction = defineAction({
  guard: "time:log",
  input: z.object({
    dateStr: z.string().regex(/^\d{4}-\d{2}-\d{2}$/).optional(),
  }),
  run: async ({ userId, access }, input: { dateStr?: string } = {}) => {
    const targetDateStr = input.dateStr || new Date().toISOString().slice(0, 10);
    const targetDayStart = new Date(targetDateStr + "T00:00:00.000Z");
    const targetDayEnd = new Date(targetDateStr + "T23:59:59.999Z");

    const [
      mapping,
      pastEntries,
      latestTodayEntry,
      dedicatedRows,
      loggedPkgRows,
      activePackages,
      activities,
      openTickets,
      openEscalations,
      userTemplates,
      openCards,
    ] = await Promise.all([
      // Linked to Bexio (links by email on the first visit when possible)
      ensureBexioLink(userId).then((linked) => (linked ? { id: 0 } : null)),

      // User's past entries: most frequent duration (D62) and most used activity
      prisma.timeEntry.findMany({
        where: { userId, deletedAt: null },
        select: { minutes: true, activityId: true },
        orderBy: { createdAt: "desc" },
        take: 50,
      }),

      // Previous entry today to get default start time (D12)
      prisma.timeEntry.findFirst({
        where: {
          userId,
          date: { gte: targetDayStart, lte: targetDayEnd },
          deletedAt: null,
          endTime: { not: null },
        },
        orderBy: [{ endTime: "desc" }, { id: "desc" }],
        select: { endTime: true },
      }),

      // Dedicated contracts for "isMine" packages
      prisma.dedicatedContract.findMany({
        where: { userId },
        select: { workPackageId: true },
      }),

      // Previously logged packages for "isMine" packages
      prisma.timeEntry.findMany({
        where: { userId, deletedAt: null },
        select: { workPackageId: true },
        distinct: ["workPackageId"],
      }),

      // All active packages
      prisma.workPackage.findMany({
        where: { archivedAt: null, ...OPEN_FOR_TIME },
        include: {
          bexioProject: {
            include: {
              client: { select: { id: true, name: true, logoUrl: true } },
            },
          },
          hubProjects: {
            where: {
              deletedAt: null,
              status: { in: ["ACTIVE", "ON_HOLD", "CLOSING"] },
            },
            select: { id: true, name: true },
            orderBy: { name: "asc" },
          },
        },
        orderBy: { name: "asc" },
      }),

      // Business activities
      prisma.businessActivity.findMany({
        orderBy: { name: "asc" },
        select: { id: true, name: true },
      }),

      // Open support tickets for side panel
      prisma.supportTicket.findMany({
        where: {
          status: { notIn: ["RESOLVED"] },
          deletedAt: null,
        },
        select: {
          id: true,
          reference: true,
          title: true,
          status: true,
          clientId: true,
        },
        orderBy: { createdAt: "desc" },
        take: 100,
      }),

      // Open escalations for side panel
      prisma.escalation.findMany({
        where: {
          status: { notIn: ["RESOLVED", "CLOSED"] },
        },
        select: {
          id: true,
          reference: true,
          shortDescription: true,
          status: true,
          clientId: true,
        },
        orderBy: { createdAt: "desc" },
        take: 100,
      }),

      // User's personal time templates (SPEC §4.3, D37)
      prisma.timeTemplate.findMany({
        where: { userId },
        include: {
          workPackage: {
            include: {
              bexioProject: {
                include: {
                  client: { select: { id: true, name: true, logoUrl: true } },
                },
              },
            },
          },
        },
        orderBy: { name: "asc" },
      }),

      // Open project cards assigned to user on running projects (SPEC §4.2, D73, T-238)
      prisma.projectCard.findMany({
        where: {
          assignees: { some: { userId } },
          status: { notIn: ["DONE", "WAIVED"] },
          project: {
            deletedAt: null,
            status: { in: ["ACTIVE", "ON_HOLD", "CLOSING"] },
          },
        },
        select: {
          id: true,
          title: true,
          status: true,
          projectId: true,
          checklist: { where: { done: false }, orderBy: { position: "asc" }, select: { id: true, title: true } },
          project: {
            select: {
              id: true,
              name: true,
              clientId: true,
              workPackageId: true,
            },
          },
        },
        orderBy: { id: "desc" },
        take: 100,
      }),
    ]);

    const isMappedToBexio = mapping !== null;

    const isPm =
      access.roleIsAdmin ||
      can(access, "pm:settings_manage") ||
      can(access, "projects:manage");

    // Compute most frequent duration (D62)
    const mostFrequentDuration = computeMostFrequentDuration(
      pastEntries.map((e) => e.minutes)
    );

    // Compute previous entry end time (D12)
    let previousEntryEndTime: string | null = null;
    if (latestTodayEntry?.endTime) {
      const d = new Date(latestTodayEntry.endTime);
      previousEntryEndTime = `${String(d.getUTCHours()).padStart(2, "0")}:${String(d.getUTCMinutes()).padStart(2, "0")}`;
    }

    // Set of user packages for "mine first" (SPEC §4.1b)
    const userPackageIds = new Set<number>();
    for (const d of dedicatedRows) userPackageIds.add(d.workPackageId);
    for (const l of loggedPkgRows) userPackageIds.add(l.workPackageId);

    // Compute live budget stats per package (D60)
    const packageOptions: PackageOption[] = await Promise.all(
      activePackages.map(async (pkg) => {
        const [totalAgg, userAgg] = await Promise.all([
          prisma.timeEntry.aggregate({
            where: { workPackageId: pkg.id, deletedAt: null },
            _sum: { minutes: true },
          }),
          prisma.timeEntry.aggregate({
            where: { workPackageId: pkg.id, userId, deletedAt: null },
            _sum: { minutes: true },
          }),
        ]);

        const totalBookedMinutes = totalAgg._sum.minutes ?? 0;
        const userBookedMinutes = userAgg._sum.minutes ?? 0;

        const stats = computePackageBudgetAndContribution({
          packageId: pkg.id,
          name: pkg.name,
          clientName: pkg.bexioProject.client?.name ?? pkg.bexioProject.name,
          budgetHours: pkg.budgetHours ? Number(pkg.budgetHours) : null,
          totalBookedMinutes,
          userBookedMinutes,
        });

        return {
          id: pkg.id,
          name: pkg.name,
          bexioProjectId: pkg.bexioProject.id,
          bexioProjectName: pkg.bexioProject.name,
          clientId: pkg.bexioProject.client?.id ?? null,
          clientName: pkg.bexioProject.client?.name ?? pkg.bexioProject.name,
          clientLogoUrl: pkg.bexioProject.client?.logoUrl ?? null,
          budgetHours: pkg.budgetHours ? Number(pkg.budgetHours) : null,
          isDedicated: pkg.isDedicated,
          isMine: userPackageIds.has(pkg.id),
          displaySummary: stats.displaySummary,
          remainingHours: stats.remainingHours,
          myUsedHours: stats.myUsedHours,
          isOverBudget: stats.isOverBudget,
          hubProjects: (pkg.hubProjects ?? []).map((p) => ({ id: p.id, name: p.name })),
        };
      })
    );

    // The person's most used activity comes first, so every form that defaults to the first one picks it (owner, 2026-11-03).
    const activityOptions: ActivityOption[] = byMostUsed(activities, pastEntries.map((e) => e.activityId)).map((a) => ({
      id: a.id,
      name: a.name,
    }));

    const sidePanelItems: SidePanelItem[] = [
      ...openCards.map((c) => ({
        type: "card" as const,
        id: c.id,
        reference: `#${c.id}`,
        title: c.title,
        status: c.status,
        clientId: c.project.clientId,
        projectId: c.projectId,
        projectName: c.project.name,
        workPackageId: c.project.workPackageId,
      })),
      ...openCards.flatMap((c) =>
        c.checklist.map((i) => ({
          type: "checklist" as const,
          id: i.id,
          reference: `#${c.id}.${i.id}`,
          title: i.title,
          status: "Checklist",
          clientId: c.project.clientId,
          projectId: c.projectId,
          projectName: c.project.name,
          workPackageId: c.project.workPackageId,
          cardId: c.id,
        })),
      ),
      ...openTickets.map((t) => ({
        type: "ticket" as const,
        id: t.id,
        reference: t.reference,
        title: t.title,
        status: t.status,
        clientId: t.clientId,
      })),
      ...openEscalations.map((e) => ({
        type: "escalation" as const,
        id: e.id,
        reference: e.reference,
        title: e.shortDescription,
        status: e.status,
        clientId: e.clientId,
      })),
    ];

    const activityMap = new Map(activities.map((a) => [a.id, a.name]));

    const templateOptions: TimeTemplateItem[] = userTemplates.map((t) => ({
      id: t.id,
      name: t.name,
      workPackageId: t.workPackageId,
      workPackageName: t.workPackage?.name ?? null,
      bexioProjectId: t.workPackage?.bexioProject.id ?? null,
      bexioProjectName: t.workPackage?.bexioProject.name ?? null,
      clientId: t.workPackage?.bexioProject.client?.id ?? null,
      clientName:
        t.workPackage?.bexioProject.client?.name ??
        t.workPackage?.bexioProject.name ??
        null,
      clientLogoUrl: t.workPackage?.bexioProject.client?.logoUrl ?? null,
      activityId: t.activityId,
      activityName: t.activityId ? (activityMap.get(t.activityId) ?? null) : null,
      billable: t.billable,
      minutes: t.minutes,
      note: t.note,
      createdAt: t.createdAt.toISOString(),
      updatedAt: t.updatedAt.toISOString(),
    }));

    return {
      isMappedToBexio,
      canChangeBillable: isPm,
      mostFrequentDuration,
      previousEntryEndTime,
      packages: packageOptions,
      activities: activityOptions,
      sidePanelItems,
      templates: templateOptions,
    };
  },
});

const updateTimeEntrySchema = z.object({
  id: z.number().int().positive("Time entry ID is required"),
  date: z.string().regex(/^\d{4}-\d{2}-\d{2}$/, "Invalid date format"),
  minutes: z.number().int().positive("Duration must be positive"),
  workPackageId: z.number().int().positive("Budget is required"),
  activityId: z.number().int().positive().optional().nullable(),
  billable: z.boolean().optional(),
  note: z.string().min(1, "Note is required"),
  startTime: z.string().regex(/^\d{2}:\d{2}$/).optional().nullable(),
  endTime: z.string().regex(/^\d{2}:\d{2}$/).optional().nullable(),
  editNote: z.string().min(1, "Edit note is required"),
  hubProjectId: z.number().int().positive().optional().nullable(),
});

export const updateTimeEntryAction = defineAction({
  guard: "time:log",
  input: updateTimeEntrySchema,
  run: async ({ userId, access }, input: z.input<typeof updateTimeEntrySchema>) => {
    const entry = await prisma.timeEntry.findUnique({
      where: { id: input.id },
      include: {
        workPackage: {
          include: { bexioProject: true },
        },
        hubProject: true,
        activity: true,
      },
    });

    if (!entry || entry.deletedAt) {
      throw new Error("Time entry not found");
    }

    const editWindowDays = await getEditWindowDays();
    const editCheck = checkTimeEntryEditability({
      entry,
      userId,
      access,
      editWindowDays,
    });

    if (!editCheck.canEdit) {
      throw new Error(editCheck.reason || "You do not have permission to edit this entry");
    }

    // Closed work package rules (SPEC §4.4, C2):
    // Existing entries on a closed package can still be corrected (duration, note),
    // but moving into a closed package is not allowed!
    if (input.workPackageId !== entry.workPackageId) {
      const targetPkg = await prisma.workPackage.findUnique({
        where: { id: input.workPackageId },
        include: { bexioProject: true },
      });
      if (!targetPkg) {
        throw new Error("Target budget not found");
      }
      if (isWorkPackageClosed(targetPkg)) {
        throw new Error("Cannot move entry into a closed budget");
      }
    }

    const dateObj = new Date(input.date + "T00:00:00.000Z");
    const times = computeStartAndEndTimes({
      minutes: input.minutes,
      customStartTime: input.startTime || undefined,
      customEndTime: input.endTime || undefined,
    });

    const [sh, sm] = times.startTime.split(":").map(Number);
    const startDateTime = new Date(dateObj);
    startDateTime.setUTCHours(sh, sm, 0, 0);

    const [eh, em] = times.endTime.split(":").map(Number);
    const endDateTime = new Date(dateObj);
    endDateTime.setUTCHours(eh, em, 0, 0);

    const isPm =
      access.isSuperAdmin ||
      access.roleIsAdmin ||
      can(access, "pm:settings_manage") ||
      can(access, "projects:manage");

    const billable = isPm ? (input.billable ?? entry.billable) : entry.billable;

    // Collect field-level changes for TimeEntryHistory (SPEC §4.4, §13)
    const historyEntries: {
      field: string;
      oldValue: string | null;
      newValue: string | null;
    }[] = [];

    if (entry.minutes !== input.minutes) {
      historyEntries.push({
        field: "minutes",
        oldValue: String(entry.minutes),
        newValue: String(input.minutes),
      });
    }

    const oldDateStr = entry.date.toISOString().slice(0, 10);
    if (oldDateStr !== input.date) {
      historyEntries.push({
        field: "date",
        oldValue: oldDateStr,
        newValue: input.date,
      });
    }

    if (entry.workPackageId !== input.workPackageId) {
      historyEntries.push({
        field: "workPackageId",
        oldValue: String(entry.workPackageId),
        newValue: String(input.workPackageId),
      });
    }

    if (entry.activityId !== (input.activityId ?? null)) {
      historyEntries.push({
        field: "activityId",
        oldValue: entry.activityId ? String(entry.activityId) : null,
        newValue: input.activityId ? String(input.activityId) : null,
      });
    }

    if (entry.billable !== billable) {
      historyEntries.push({
        field: "billable",
        oldValue: String(entry.billable),
        newValue: String(billable),
      });
    }

    if ((entry.note ?? "").trim() !== input.note.trim()) {
      historyEntries.push({
        field: "note",
        oldValue: entry.note ?? null,
        newValue: input.note.trim(),
      });
    }

    let oldStartStr: string | null = null;
    if (entry.startTime) {
      const d = new Date(entry.startTime);
      oldStartStr = `${String(d.getUTCHours()).padStart(2, "0")}:${String(d.getUTCMinutes()).padStart(2, "0")}`;
    }
    if (oldStartStr !== times.startTime) {
      historyEntries.push({
        field: "startTime",
        oldValue: oldStartStr,
        newValue: times.startTime,
      });
    }

    let oldEndStr: string | null = null;
    if (entry.endTime) {
      const d = new Date(entry.endTime);
      oldEndStr = `${String(d.getUTCHours()).padStart(2, "0")}:${String(d.getUTCMinutes()).padStart(2, "0")}`;
    }
    if (oldEndStr !== times.endTime) {
      historyEntries.push({
        field: "endTime",
        oldValue: oldEndStr,
        newValue: times.endTime,
      });
    }

    const cleanEditNote = input.editNote.trim();
    if (historyEntries.length === 0) {
      await prisma.timeEntryHistory.create({
        data: {
          timeEntryId: entry.id,
          changedBy: userId,
          field: "note",
          oldValue: entry.note ?? null,
          newValue: input.note.trim(),
          editNote: cleanEditNote,
        },
      });
    } else {
      for (const h of historyEntries) {
        await prisma.timeEntryHistory.create({
          data: {
            timeEntryId: entry.id,
            changedBy: userId,
            field: h.field,
            oldValue: h.oldValue,
            newValue: h.newValue,
            editNote: cleanEditNote,
          },
        });
      }
    }

    const nextHubProjectId =
      input.workPackageId !== entry.workPackageId
        ? await entryProjectId({
            workPackageId: input.workPackageId,
            hubProjectId: input.hubProjectId,
            cardId: entry.cardId,
          })
        : input.hubProjectId !== undefined
          ? await entryProjectId({
              workPackageId: entry.workPackageId,
              hubProjectId: input.hubProjectId,
              cardId: entry.cardId,
            })
          : undefined;

    const updated = await prisma.timeEntry.update({
      where: { id: entry.id },
      data: {
        date: dateObj,
        minutes: input.minutes,
        workPackageId: input.workPackageId,
        activityId: input.activityId ?? null,
        billable,
        note: input.note.trim(),
        startTime: startDateTime,
        endTime: endDateTime,
        syncStatus: "QUEUED",
        ...(nextHubProjectId !== undefined ? { hubProjectId: nextHubProjectId } : {}),
      },
    });

    // Re-queue background sync to Bexio (SPEC §4.4, D11)
    await queueTimeEntrySync(entry.id);

    // Evaluate budget thresholds, alerts and over-budget flag (SPEC §4.5)
    await evaluatePackageBudget(input.workPackageId);
    if (entry.workPackageId !== input.workPackageId) {
      await evaluatePackageBudget(entry.workPackageId);
    }

    const serializedEntry: RawGridEntryInput = {
      id: updated.id,
      date: input.date,
      startTime: times.startTime,
      endTime: times.endTime,
      minutes: updated.minutes,
      note: updated.note,
      syncStatus: updated.syncStatus,
      isAvailability: updated.isAvailability,
      isOverBudget: updated.isOverBudget,
      source: updated.source,
      billable: updated.billable,
      workPackageId: updated.workPackageId,
      activity: input.activityId ? { id: input.activityId, name: "" } : null,
      bexioMessage: null,
      bexioId: updated.bexioId,
    };

    return { ok: true, id: entry.id, entry: serializedEntry };
  },
  tag: CACHE_TAGS.settings,
  paths: () => ["/bexio"],
});

const deleteTimeEntrySchema = z.object({
  id: z.number().int().positive("Time entry ID is required"),
  editNote: z.string().min(1, "Reason for deletion is required"),
  /** Owner, 2026-11-04: deleting time logged on a Done task can also reopen it (asked in the delete popup, off by default). */
  reopenCard: z.boolean().optional(),
});

/** The Done task a time entry was logged on, so the delete popup can offer to reopen it. */
export const getEntryDoneTask = defineAction({
  guard: "time:log",
  input: z.object({ id: z.number().int().positive() }),
  run: async (_ctx, input: { id: number }) => {
    const entry = await prisma.timeEntry.findUnique({ where: { id: input.id }, select: { cardId: true } });
    if (!entry?.cardId) return { ok: true as const, task: null };
    const card = await prisma.projectCard.findUnique({ where: { id: entry.cardId }, select: { id: true, title: true, status: true, projectId: true } });
    return { ok: true as const, task: card && card.status === "DONE" ? { id: card.id, title: card.title, projectId: card.projectId } : null };
  },
});

export const deleteTimeEntryAction = defineAction({
  guard: "time:log",
  input: deleteTimeEntrySchema,
  run: async ({ userId, access }, input: z.input<typeof deleteTimeEntrySchema>) => {
    const entry = await prisma.timeEntry.findUnique({
      where: { id: input.id },
      include: {
        workPackage: {
          include: { bexioProject: true },
        },
      },
    });

    if (!entry || entry.deletedAt) {
      throw new Error("Time entry not found");
    }

    const editWindowDays = await getEditWindowDays();
    const editCheck = checkTimeEntryEditability({
      entry,
      userId,
      access,
      editWindowDays,
    });

    if (!editCheck.canDelete) {
      throw new Error(editCheck.reason || "You do not have permission to delete this entry");
    }

    await prisma.timeEntry.update({
      where: { id: entry.id },
      data: {
        deletedAt: new Date(),
        deletedBy: userId,
        syncStatus: "QUEUED",
      },
    });

    await prisma.timeEntryHistory.create({
      data: {
        timeEntryId: entry.id,
        changedBy: userId,
        field: "deleted",
        oldValue: "active",
        newValue: "deleted",
        editNote: input.editNote.trim(),
      },
    });

    await queueTimeEntrySync(entry.id);

    // Evaluate budget after deletion (SPEC §4.5)
    await evaluatePackageBudget(entry.workPackageId);

    let reopened = false;
    if (input.reopenCard && entry.cardId) {
      const card = await prisma.projectCard.findUnique({ where: { id: entry.cardId }, select: { projectId: true, status: true } });
      if (card?.status === "DONE") {
        const r = await setCardStatus({ projectId: card.projectId, cardId: entry.cardId, status: "IN_PROGRESS" });
        if (r.ok) {
          // The "time differed / skipped" note was about finishing; the task is open again.
          await prisma.cardTimeChange.deleteMany({ where: { cardId: entry.cardId } });
          reopened = true;
        }
      }
    }

    return { ok: true, id: entry.id, reopened };
  },
  tag: CACHE_TAGS.settings,
  paths: () => ["/bexio"],
});

const moveTimeEntrySchema = z.object({
  timeEntryIds: z.array(z.number().int().positive()).min(1, "Select at least one entry"),
  targetWorkPackageId: z.number().int().positive("Target package is required"),
  editNote: z.string().min(1, "Move note is required"),
});

export const moveTimeEntryAction = defineAction({
  guard: "time:log",
  input: moveTimeEntrySchema,
  run: async ({ userId, access }, input: z.input<typeof moveTimeEntrySchema>) => {
    const targetPkg = await prisma.workPackage.findUnique({
      where: { id: input.targetWorkPackageId },
      include: { bexioProject: true },
    });
    if (!targetPkg) {
      throw new Error("Target budget not found");
    }
    if (isWorkPackageClosed(targetPkg)) {
      throw new Error("Cannot move entries into a closed budget");
    }

    const editWindowDays = await getEditWindowDays();
    const entries = await prisma.timeEntry.findMany({
      where: {
        id: { in: input.timeEntryIds },
        deletedAt: null,
      },
      include: {
        workPackage: true,
      },
    });

    if (entries.length === 0) {
      throw new Error("No valid time entries found to move");
    }

    let movedCount = 0;
    const sourcePackageIds = new Set<number>();
    for (const entry of entries) {
      const editCheck = checkTimeEntryEditability({
        entry,
        userId,
        access,
        editWindowDays,
      });

      if (!editCheck.canMove) {
        throw new Error(
          `Cannot move entry #${entry.id}: ${editCheck.reason || "Permission denied"}`
        );
      }

      if (entry.workPackageId === input.targetWorkPackageId) {
        continue;
      }

      sourcePackageIds.add(entry.workPackageId);

      await prisma.timeEntryHistory.create({
        data: {
          timeEntryId: entry.id,
          changedBy: userId,
          field: "workPackageId",
          oldValue: `${entry.workPackage.name} (#${entry.workPackageId})`,
          newValue: `${targetPkg.name} (#${targetPkg.id})`,
          editNote: input.editNote.trim(),
        },
      });

      const nextHubProjectId = await entryProjectId({
        workPackageId: input.targetWorkPackageId,
        cardId: entry.cardId,
      });

      await prisma.timeEntry.update({
        where: { id: entry.id },
        data: {
          workPackageId: input.targetWorkPackageId,
          hubProjectId: nextHubProjectId,
          syncStatus: "QUEUED",
        },
      });

      await queueTimeEntrySync(entry.id);
      movedCount++;
    }

    // Both budgets update (SPEC §4.4b, round 22)
    for (const sourceId of sourcePackageIds) {
      await evaluatePackageBudget(sourceId);
    }
    await evaluatePackageBudget(input.targetWorkPackageId);

    return { ok: true, movedCount };
  },
  tag: CACHE_TAGS.settings,
  paths: () => ["/bexio"],
});

const requestBudgetChangeSchema = z.object({
  projectId: z.number().int().positive(),
  requestedHours: z.number().positive("Enter a requested hour budget above zero"),
  note: z.string().trim().min(1, "Please provide a reason or note for the budget change"),
});

/**
 * PM manager requests a budget change from PM admin (SPEC §4.5, D46).
 */
export const requestBudgetChangeAction = defineAction({
  guard: "projects:manage",
  input: requestBudgetChangeSchema,
  run: async ({ userId }, input: z.input<typeof requestBudgetChangeSchema>) => {
    const project = await prisma.hubProject.findUnique({
      where: { id: input.projectId },
      include: { client: true, workPackage: true },
    });
    if (!project) throw new Error("Project not found");

    const requester = await prisma.user.findUnique({
      where: { id: userId },
      select: { name: true, email: true },
    });
    const requesterName = requester?.name || requester?.email || "PM Manager";

    const admins = await usersWithPermission("pm:settings_manage");
    if (admins.length > 0) {
      await notify({
        type: "project_budget_warning",
        category: "projects",
        recipients: admins,
        emailHeading: `Budget change requested: ${project.name}`,
        message: `${requesterName} requested a budget change for "${project.name}" (${project.client.name}): ${input.requestedHours} h (currently ${Number(project.budgetHours)} h). Note: ${input.note}`,
        link: `/projects/${project.id}?tab=time`,
      });
    }

    return { ok: true };
  },
  tag: CACHE_TAGS.settings,
});

export const getTimeEntryDetailsAction = defineAction({
  guard: "time:log",
  input: z.object({ id: z.number().int().positive() }),
  run: async ({ userId, access }, input: { id: number }): Promise<TimeEntryDetails> => {
    const entry = await prisma.timeEntry.findUnique({
      where: { id: input.id },
      include: {
        workPackage: {
          include: {
            bexioProject: {
              include: {
                client: {
                  select: { id: true, name: true, logoUrl: true },
                },
              },
            },
            hubProjects: {
              where: {
                deletedAt: null,
                status: { in: ["ACTIVE", "ON_HOLD", "CLOSING"] },
              },
              select: { id: true, name: true },
              orderBy: { name: "asc" },
            },
          },
        },
        hubProject: {
          select: { status: true },
        },
        activity: {
          select: { id: true, name: true },
        },
        user: {
          select: { id: true, name: true },
        },
      },
    });

    if (!entry || entry.deletedAt) {
      throw new Error("Time entry not found");
    }

    const editWindowDays = await getEditWindowDays();
    const editCheck = checkTimeEntryEditability({
      entry,
      userId,
      access,
      editWindowDays,
    });

    const isPackageClosed = isWorkPackageClosed({
      ...entry.workPackage,
      hubProject: entry.hubProject,
    });

    let startTimeStr: string | null = null;
    let endTimeStr: string | null = null;
    if (entry.startTime) {
      const d = new Date(entry.startTime);
      startTimeStr = `${String(d.getUTCHours()).padStart(2, "0")}:${String(d.getUTCMinutes()).padStart(2, "0")}`;
    }
    if (entry.endTime) {
      const d = new Date(entry.endTime);
      endTimeStr = `${String(d.getUTCHours()).padStart(2, "0")}:${String(d.getUTCMinutes()).padStart(2, "0")}`;
    }

    const client = entry.workPackage.bexioProject.client;
    const clientName = client?.name ?? entry.workPackage.bexioProject.name;

    return {
      id: entry.id,
      date: entry.date.toISOString().slice(0, 10),
      startTime: startTimeStr,
      endTime: endTimeStr,
      minutes: entry.minutes,
      workPackageId: entry.workPackage.id,
      workPackageName: entry.workPackage.name,
      bexioProjectId: entry.workPackage.bexioProject.id,
      bexioProjectName: entry.workPackage.bexioProject.name,
      clientId: client?.id ?? null,
      clientName,
      clientLogoUrl: client?.logoUrl ?? null,
      activityId: entry.activity?.id ?? null,
      activityName: entry.activity?.name ?? null,
      billable: entry.billable,
      note: entry.note,
      source: entry.source,
      isAvailability: entry.isAvailability,
      syncStatus: entry.syncStatus,
      bexioId: entry.bexioId,
      userId: entry.userId,
      userName: entry.user.name,
      isPackageClosed,
      canEdit: editCheck.canEdit,
      canDelete: editCheck.canDelete,
      canMove: editCheck.canMove,
      editRestrictionReason: editCheck.reason ?? null,
      createdAt: entry.createdAt.toISOString(),
      hubProjectId: entry.hubProjectId ?? null,
      hubProjects: (entry.workPackage?.hubProjects ?? []).map((p) => ({ id: p.id, name: p.name })),
    };
  },
});

export const getTimeEntryHistoryAction = defineAction({
  guard: "time:log",
  input: z.object({ timeEntryId: z.number().int().positive() }),
  run: async ({ userId, access }, input: { timeEntryId: number }): Promise<TimeEntryHistoryItem[]> => {
    const entry = await prisma.timeEntry.findUnique({
      where: { id: input.timeEntryId },
      select: { id: true, userId: true },
    });
    if (!entry) {
      throw new Error("Time entry not found");
    }

    const isAuthor = entry.userId === userId;
    const isPm =
      access.isSuperAdmin ||
      access.roleIsAdmin ||
      can(access, "pm:settings_manage") ||
      can(access, "projects:manage") ||
      can(access, "projects:view_all");

    if (!isAuthor && !isPm) {
      throw new Error("You do not have permission to view this entry's history");
    }

    const historyRows = await prisma.timeEntryHistory.findMany({
      where: { timeEntryId: input.timeEntryId },
      orderBy: { createdAt: "desc" },
    });

    const userIds = Array.from(new Set(historyRows.map((h) => h.changedBy)));
    const users = await prisma.user.findMany({
      where: { id: { in: userIds } },
      select: { id: true, name: true },
    });
    const userMap = new Map(users.map((u) => [u.id, u.name]));

    return historyRows.map((h) => ({
      id: h.id,
      timeEntryId: h.timeEntryId,
      changedBy: h.changedBy,
      changedByName: userMap.get(h.changedBy) || `User #${h.changedBy}`,
      field: h.field,
      oldValue: h.oldValue,
      newValue: h.newValue,
      editNote: h.editNote,
      createdAt: h.createdAt.toISOString(),
    }));
  },
});

const getWeekEntriesSchema = z.object({
  week: z.string().regex(/^\d{4}-\d{2}-\d{2}$/, "Invalid week date format"),
});

export const getWeekEntriesAction = defineAction({
  guard: "time:log",
  input: getWeekEntriesSchema,
  run: async ({ userId }, input: z.input<typeof getWeekEntriesSchema>) => {
    const refDate = new Date(input.week + "T00:00:00.000Z");
    const dayOfWeek = refDate.getUTCDay();
    const diffToMonday = dayOfWeek === 0 ? -6 : 1 - dayOfWeek;
    const weekStart = new Date(refDate);
    weekStart.setUTCDate(refDate.getUTCDate() + diffToMonday);
    weekStart.setUTCHours(0, 0, 0, 0);

    const weekEnd = new Date(weekStart);
    weekEnd.setUTCDate(weekStart.getUTCDate() + 6);
    weekEnd.setUTCHours(23, 59, 59, 999);

    const weekRawEntries = await prisma.timeEntry.findMany({
      where: {
        userId,
        date: { gte: weekStart, lte: weekEnd },
        deletedAt: null,
      },
      include: {
        activity: { select: { id: true, name: true } },
        syncAttempts: {
          orderBy: { createdAt: "desc" },
          take: 1,
        },
      },
      orderBy: [{ date: "asc" }, { startTime: "asc" }, { id: "asc" }],
      take: 200,
    });

    const entries: RawGridEntryInput[] = weekRawEntries.map((e) => {
      let startTimeStr: string | null = null;
      let endTimeStr: string | null = null;
      if (e.startTime) {
        const d = new Date(e.startTime);
        startTimeStr = `${String(d.getUTCHours()).padStart(2, "0")}:${String(d.getUTCMinutes()).padStart(2, "0")}`;
      }
      if (e.endTime) {
        const d = new Date(e.endTime);
        endTimeStr = `${String(d.getUTCHours()).padStart(2, "0")}:${String(d.getUTCMinutes()).padStart(2, "0")}`;
      }

      const latestAttempt = e.syncAttempts[0];
      const bexioMessage = latestAttempt?.error ?? null;

      return {
        id: e.id,
        date: e.date.toISOString().slice(0, 10),
        startTime: startTimeStr,
        endTime: endTimeStr,
        minutes: e.minutes,
        note: e.note,
        syncStatus: e.syncStatus,
        isAvailability: e.isAvailability,
        isOverBudget: e.isOverBudget,
        source: e.source,
        billable: e.billable,
        workPackageId: e.workPackageId,
        activity: e.activity ? { id: e.activity.id, name: e.activity.name } : null,
        bexioMessage,
        bexioId: e.bexioId,
      };
    });

    const weekData = getWeekDays(refDate, weekRawEntries, new Date());

    return {
      ok: true,
      entries,
      weekData,
    };
  },
});
